import { PermissionGate } from "@components/PermissionGate";
import { COUNTRY_CODES } from "@constants/countryCodes";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Button,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  ScrollAreaAutosize,
  Select,
  Tabs,
  Text,
} from "@mantine/core";
import { DatePickerInput, DateTimePicker } from "@mantine/dates";
import {
  IconArrowLeft,
  IconArrowRight,
  IconCalendar,
  IconClock,
  IconInfoCircle,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { useShallow } from "zustand/react/shallow";

import { CustomPhoneInput } from "@components/CustomPhoneInput";
import { MultiSelectWithSearch } from "@components/MultiSelectWithSearch";
import { PatientsSelect } from "@components/PatientsSelect";

import {
  formatDateToString,
  isValidDate,
  parseDate,
} from "@utils/dateValidation";
import { handleOnChange } from "@utils/handleOnChange";
import { prefixPhoneNumberWithPlus } from "@utils/phoneNumberPrefix";
import { truncateWithEllipsis } from "@utils/truncateText";

import {
  getAppointmentCategoriesValues,
  getGenderValues,
  getPaymentMethods,
} from "@data/dropdownValues";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import {
  MAX_FILENAME_LENGTH_BEFORE_UPLOAD,
  MAX_PAGE_SIZE,
  MEGABYTE,
} from "@constants/index";

import { useCreateAppointment } from "@hooks/mutations/appointment/useCreateAppointment";
import { useGetAppointmentSlots } from "@hooks/queries/appointment/useGetAppointmentSlots";
import { useGetClinicConsultationServices } from "@hooks/queries/clinic/useGetClinicConsultationServices";
import { useGetClinicLabs } from "@hooks/queries/clinic/useGetClinicLabs";
import { useGetPatientByPhoneNumber } from "@hooks/queries/patient/useGetPatientByPhoneNumber";
import { useGetPatients } from "@hooks/queries/patient/useGetPatients";
import { useGetSinglePatient } from "@hooks/queries/patient/useGetSinglePatient";
import { useGetDoctorsForConsultationService } from "@hooks/queries/user/useGetDoctorsForConsultationService";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useAppStore } from "@hooks/store/useAppStore";

import {
  Appointment,
  AppointmentSlot,
  HomeCareDetail,
  TeleconsultationAppointmentDetail,
} from "@customTypes/appointment";

import SearchIllustration from "@assets/misc/search-rafiki.svg?react";

import { StyledCreateAppointmentModalContent } from "./CreateAppointmentModal.styled";


interface CreateAppointmentDialogProps {
  preCurrentTab?: string; //* Using "pre" to mean supplied before the fact because this modal can be invoked from outside the appointment context now
  prePatientId?: string; // ? Is this even needed, as I can easily get this from the URL - Don't use this to check if modal was invoked from outside appointment context
  opened: boolean;
  close: () => void;
}

// Utility type to help make form manipulation easier
type ShallowForm = Omit<Appointment, "details"> &
  TeleconsultationAppointmentDetail &
  HomeCareDetail;

const DEFAULT_TOTAL_STEPS = 2;
const LAB_TOTAL_STEPS = 3;

export const CreateAppointmentModal = ({
  preCurrentTab,
  prePatientId,
  opened,
  close,
}: CreateAppointmentDialogProps) => {
  const { t, i18n } = useTranslation();
  const [storeTabs, setStoreTabs] = useAppStore(
    useShallow(state => [state.tabs, state.setTabs])
  );
  const { consultationId } = useParams();

  const [pricingCountry, setPricingCountry] = useState<string | null>(null);
  const [newPatientStep, setNewPatientStep] = useState(1);
  const [existingPatientStep, setExistingPatientStep] = useState(1);
  const [currentTab, setCurrentTab] = useState<string | null>(
    preCurrentTab || storeTabs.createAppointmentTab || "new"
  );
  const [searchFilter, setSearchFilter] = useState<string | null>(null); // todo: Rename this to something about patient id?
  const [showExistingPatientForm, setShowExistingPatientForm] = useState(false);
  const [dob, setDob] = useState<Date | null>(null);
  const [gender, setGender] = useState<string | null>(null);
  const [doctor, setDoctor] = useState<ComboboxItem | null>(null);
  const [appointmentDate, setAppointmentDate] = useState<Date | null>(
    new Date(new Date().setHours(8, 0, 0, 0))
  );
  const [appointmentCategory, setAppointmentCategory] =
    useState<ComboboxItem | null>(null);
  const [serviceType, setServiceType] = useState<ComboboxItem | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneInputError, setIsPhoneInputError] = useState(false);
  const [formData, setFormData] = useState<Partial<ShallowForm>>({});
  const [labTypes, setLabTypes] = useState<ComboboxItem[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [selectedSlotId, setSelectedSlotId] = useState("");

  const {
    isLoading: isGetPatientLoading,
    isSuccess: isGetPatientSuccess,
    data: getPatientData,
    refetch: getPatientRefetch,
    fetchStatus: getPatientFetchStatus,
  } = useGetSinglePatient({
    patientId: prePatientId ?? searchFilter ?? "",
    isEnabled: false,
  });
  const { mutate: createAppointment, isLoading: isCreateAppointmentLoading } =
    useCreateAppointment();
  const { data: patientsData, isLoading: isPatientsLoading } = useGetPatients({
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE,
    },
    fields: ["patient_id", "first_name", "surname"],
  });
  const {
    data: doctorsForConsultationData,
    isLoading: isDoctorsForConsultationLoading,
    fetchStatus: doctorsForConsultationFetchStatus,
    isSuccess: isDoctorsForConsultationSuccess,
  } = useGetDoctorsForConsultationService({
    role: "doctor",
    serviceId: serviceType?.value,
    isEnabled: !!serviceType?.value,
  });
  const { data: doctorsData } = useGetUsers({ role: "doctor" });
  const {
    data: appointmentSlotsData,
    isLoading: isAppointmentSlotsLoading,
    refetch: refetchAppointmentSlots,
  } = useGetAppointmentSlots({
    doctorAssigned: doctor?.value ?? "",
    withDoctorAssigned: true,
    booked: false,
    pagination: { page: 1, pageSize: MAX_PAGE_SIZE },
    isEnabled:
      !!doctor?.value &&
      !!serviceType?.value &&
      appointmentCategory?.value !== "lab",
  });

  const availableAppointmentSlots = useMemo(
    () =>
      (appointmentSlotsData?.newData ?? []).filter(
        slot => slot.quantity > 0 && slot.startTime > new Date()
      ),
    [appointmentSlotsData]
  );
  const { data: clinicLabsData } = useGetClinicLabs();

  const patientsComboboxItems = useMemo(() => {
    return patientsData?.newData?.map(item => ({
      label: `${item.firstName} ${
        item.middleName ? item.middleName + " " : ""
      }${item.surname}`,
      value: item.patientId as string,
    }));
  }, [patientsData]);

  const doctorsComboboxItems = useMemo(() => {
    return doctorsData?.map(item => ({
      value: item.id,
      label: item.fullName,
    }));
  }, [doctorsData]);

  const handleSlotConflict = (error: any) => {
    if (error?.response?.status === 409 && !String(error?.response?.data?.detail).toLowerCase().includes("pric")) {
      setSelectedSlotId("");
      setAppointmentDate(null);
      refetchAppointmentSlots();
    }
  };

  const processFormToSubmit = () => {
    const jsFormData = new FormData();
    if (pricingCountry) jsFormData.set("pricing_country", pricingCountry);

    if (uploadedFiles?.length) {
      uploadedFiles.forEach((file: File) => {
        jsFormData.append(`files`, file);
      });
    }
    jsFormData.append("category", appointmentCategory?.value ?? "");
    jsFormData.append("status", "booked");
    jsFormData.append("doctor_assigned", doctor?.value ?? "");
    jsFormData.append("is_follow_up", `${!!preCurrentTab}`);
    jsFormData.append("language", `${i18n.language}`);
    jsFormData.append(
      "consultation_id",
      `${preCurrentTab ? consultationId : ""}`
    );
    jsFormData.append("medium", "emr");
    jsFormData.append("priority", "scheduled");
    jsFormData.append("appointment_date", appointmentDate?.toISOString() ?? "");
    if (selectedSlotId) jsFormData.append("slot_id", selectedSlotId);

    if (appointmentCategory?.value === "teleconsultation") {
      const details = {
        appointment_type: "teleconsultation",
        t_service_date: appointmentDate?.toISOString(), // Will be deprecated soon
        phone_number: prefixPhoneNumberWithPlus(formData?.phoneNumber),
        whatsapp_phone_number: prefixPhoneNumberWithPlus(formData?.phoneNumber),
        first_name: formData?.firstName,
        surname: formData?.surname,
        dob: dob ? formatDateToString(dob) : "",
        gender: formData?.gender,
        t_service_type: serviceType?.label,
        t_service_type_id: serviceType?.value,
        t_payment_method: paymentMethod ?? "",
        t_city: formData?.city,
        t_additional_health_info: "",
      };

      jsFormData.append("details", JSON.stringify(details));
      createAppointment(
        { formData: jsFormData },
        {
          onSuccess: () => {
            if (!preCurrentTab) {
              setFormData({} as Partial<ShallowForm>);
              setShowExistingPatientForm(false);
            }
            setExistingPatientStep(1);
            setNewPatientStep(1);
            setAppointmentDate(new Date());
            setAppointmentCategory(null);
            setServiceType(null);
            setPaymentMethod(null);
            setPhoneNumber("");
            setIsPhoneInputError(false);
            setSearchFilter("");
            setDoctor(null);
            close();
          },
          onError: handleSlotConflict,
        }
      );
    } else if (appointmentCategory?.value === "homecare") {
      const details = {
        appointment_type: "homecare",
        service_date: appointmentDate?.toISOString(), // Will be deprecated soon
        phone_number: prefixPhoneNumberWithPlus(formData?.phoneNumber),
        whatsapp_phone_number: prefixPhoneNumberWithPlus(formData?.phoneNumber),
        first_name: formData?.firstName,
        surname: formData?.surname,
        dob: dob ? formatDateToString(dob) : "",
        gender: formData?.gender,
        service_type: serviceType?.label,
        service_type_id: serviceType?.value,
        payment_method: paymentMethod ?? "",
        city: formData?.city,
        zone: formData?.zone,
        additional_health_info: "",
        has_insurance: "false",
      };
      jsFormData.append("details", JSON.stringify(details));

      createAppointment(
        { formData: jsFormData },
        {
          onSuccess: () => {
            if (!preCurrentTab) {
              setFormData({} as Partial<ShallowForm>);
              setShowExistingPatientForm(false);
            }
            setExistingPatientStep(1);
            setNewPatientStep(1);
            setAppointmentDate(new Date());
            setAppointmentCategory(null);
            setServiceType(null);
            setPaymentMethod(null);
            setPhoneNumber("");
            setIsPhoneInputError(false);
            setGender(null);
            setSearchFilter("");
            setDoctor(null);
            close();
          },
          onError: handleSlotConflict,
        }
      );
    } else if (appointmentCategory?.value === "lab") {
      const details = {
        appointment_type: "lab",
        l_service_date: appointmentDate?.toISOString(), // Will be deprecated soon
        phone_number: prefixPhoneNumberWithPlus(formData?.phoneNumber),
        whatsapp_phone_number: prefixPhoneNumberWithPlus(formData?.phoneNumber),
        first_name: formData?.firstName,
        surname: formData?.surname,
        dob: dob ? formatDateToString(dob) : "",
        gender: formData?.gender,
        L_service_type: serviceType?.label,
        l_service_type_ids: labTypes?.map(lab => lab.value),
        l_payment_method: paymentMethod ?? "",
        l_city: formData?.city,
        l_additional_health_info: "",
        has_insurance: "false",
      };      jsFormData.append("details", JSON.stringify(details));

      console.group("jsFormData");
      jsFormData.forEach((value, key) => {
        console.log(`${key}: ${value}`);
      });
      console.groupEnd();

      createAppointment(
        { formData: jsFormData },
        {
          onSuccess: () => {
            if (!preCurrentTab) {
              setFormData({} as Partial<ShallowForm>);
              setShowExistingPatientForm(false);
            }
            setExistingPatientStep(1);
            setNewPatientStep(1);
            setAppointmentDate(new Date());
            setAppointmentCategory(null);
            setServiceType(null);
            setPaymentMethod(null);
            setPhoneNumber("");
            setIsPhoneInputError(false);
            setGender(null);
            setSearchFilter("");
            setDoctor(null);
            setUploadedFiles([]);
            setLabTypes([]);
            close();
          },
          onError: handleSlotConflict,
        }
      );
    }
  };

  const totalSteps =
    appointmentCategory?.value === "lab"
      ? LAB_TOTAL_STEPS
      : DEFAULT_TOTAL_STEPS;

  const isFinalStep =
    (currentTab === "new" && newPatientStep === totalSteps) ||
    (currentTab === "existing" && existingPatientStep === totalSteps);
  const requiresDoctorAvailability =
    appointmentCategory?.value !== "lab" && isFinalStep;
  const isAvailabilityInvalid =
    requiresDoctorAvailability &&
    (!doctor?.value ||
      !serviceType?.value ||
      !appointmentDate ||
      !selectedSlotId);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentTab === "existing") {
      if (existingPatientStep === totalSteps) {
        processFormToSubmit();
      } else {
        setExistingPatientStep(existingPatientStep + 1);
      }
    } else if (currentTab === "new") {
      if (newPatientStep === totalSteps) {
        processFormToSubmit();
      } else {
        setNewPatientStep(newPatientStep + 1);
      }
    }
    e.stopPropagation();
  };

  useEffect(() => {
    if (gender) {
      setFormData({
        ...formData,
        gender,
      });
    }
  }, [gender]);

  useEffect(() => {
    if (phoneNumber) {
      setFormData({
        ...formData,
        phoneNumber,
      });
    }
  }, [phoneNumber]);

  useEffect(() => {
    setSelectedSlotId("");
    if (appointmentCategory?.value !== "lab") setAppointmentDate(null);
  }, [doctor?.value, serviceType?.value, appointmentCategory?.value]);

  useEffect(() => {
    if (getPatientData) {
      setFormData({
        firstName: getPatientData.firstName,
        surname: getPatientData.surname,
        phoneNumber: getPatientData.phoneNumber,
      });
      setGender(getPatientData.gender);

      const dob = isValidDate(getPatientData.dob as string)
        ? parseDate(getPatientData.dob as string)
        : null;
      setDob(dob);
      // console.log("[Get patient data]", getPatientData);
    }
  }, [getPatientData]);

  useEffect(() => {
    if (prePatientId) {
      getPatientRefetch();
    }
  }, []);

  useEffect(() => {
    if (isGetPatientSuccess) {
      setShowExistingPatientForm(true);
    }
  }, [isGetPatientSuccess]);

  return (
    <Modal
      centered
      size={"lg"}
      title={<Text fw={500}>{t("create-appointment")}</Text>}
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledCreateAppointmentModalContent>
        <Select mb="md" clearable searchable value={pricingCountry} onChange={setPricingCountry}
          label={t("patient-current-country", "Patient’s current country")}
          placeholder={t("unknown-country-default-price", "Unknown — use default price")}
          data={COUNTRY_CODES} />
        <Tabs
          value={currentTab}
          onChange={value => {
            setNewPatientStep(1);
            setExistingPatientStep(1);
            setFormData({} as Partial<ShallowForm>); // Clear form to prevent carrying over wrong data
            setGender(null);
            setDoctor(null);
            setAppointmentCategory(null);
            setPaymentMethod(null);
            setPhoneNumber("");
            setIsPhoneInputError(false);
            setAppointmentDate(new Date());
            setServiceType(null);
            if (currentTab === "existing") {
              setShowExistingPatientForm(false);
              setSearchFilter("");
            }
            setCurrentTab(value);
            setStoreTabs({ createAppointmentTab: value as string });
            setLabTypes([]);
            setUploadedFiles([]);
          }}>
          <Tabs.List grow>
            <Tabs.Tab value="new" disabled={!!preCurrentTab}>
              {t("new-patient")}
            </Tabs.Tab>
            <Tabs.Tab value="existing">{t("existing-patient")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={"new"}>
            <form className="form" onSubmit={handleFormSubmit}>
              <p className="step-label">
                {t("step")} {newPatientStep}
                {newPatientStep > 1 &&
                appointmentCategory?.value === "teleconsultation"
                  ? " - " + t("teleconsultation-appointment")
                  : null}
                {newPatientStep > 1 && appointmentCategory?.value === "homecare"
                  ? " - " + t("homecare-appointment")
                  : null}
                {newPatientStep > 1 &&
                newPatientStep < 3 &&
                appointmentCategory?.value === "lab"
                  ? " - " + t("analysis-appointment")
                  : null}
                {newPatientStep > 2 && appointmentCategory?.value === "lab"
                  ? " - " + t("analysis-prescription-documents")
                  : null}
              </p>

              {newPatientStep === 1 ? (
                <PersonalInfoStep
                  formData={formData}
                  setFormData={setFormData}
                  dob={dob}
                  setDob={setDob}
                  setGender={setGender}
                  gender={gender}
                  setAppointmentCategory={setAppointmentCategory}
                  setIsPhoneInputError={setIsPhoneInputError}
                  setPhoneNumber={setPhoneNumber}
                />
              ) : null}

              {newPatientStep === 2 ? (
                (appointmentCategory?.value as string) ===
                "teleconsultation" ? (
                  <TeleconsultationAppointmentStep
                    doctor={doctor}
                    formData={formData}
                    setFormData={setFormData}
                    setDoctor={setDoctor}
                    appointmentDate={appointmentDate}
                    setAppointmentDate={setAppointmentDate}
                    serviceType={serviceType}
                    setServiceType={setServiceType}
                    setPaymentMethod={setPaymentMethod}
                    doctorsForConsultationData={
                      doctorsForConsultationData ?? []
                    }
                    isDoctorsForConsultationLoading={
                      isDoctorsForConsultationLoading &&
                      doctorsForConsultationFetchStatus === "fetching"
                    }
                    isDoctorsForConsultationSuccess={
                      isDoctorsForConsultationSuccess
                    }
                    prePatientId={prePatientId}
                    appointmentCategory={appointmentCategory}
                    slots={availableAppointmentSlots}
                    isSlotsLoading={isAppointmentSlotsLoading}
                    selectedSlotId={selectedSlotId}
                    setSelectedSlotId={setSelectedSlotId}
                  />
                ) : appointmentCategory?.value === "homecare" ? (
                  <HomecareAppointmentStep
                    formData={formData}
                    setFormData={setFormData}
                    serviceType={serviceType}
                    setServiceType={setServiceType}
                    appointmentDate={appointmentDate}
                    setAppointmentDate={setAppointmentDate}
                    doctor={doctor}
                    setDoctor={setDoctor}
                    setPaymentMethod={setPaymentMethod}
                    doctorsForConsultationData={
                      doctorsForConsultationData ?? []
                    }
                    isDoctorsForConsultationLoading={
                      isDoctorsForConsultationLoading &&
                      doctorsForConsultationFetchStatus === "fetching"
                    }
                    isDoctorsForConsultationSuccess={
                      isDoctorsForConsultationSuccess
                    }
                    prePatientId={prePatientId}
                    appointmentCategory={appointmentCategory}
                    slots={availableAppointmentSlots}
                    isSlotsLoading={isAppointmentSlotsLoading}
                    selectedSlotId={selectedSlotId}
                    setSelectedSlotId={setSelectedSlotId}
                  />
                ) : (
                  <LabAppointmentStep
                    formData={formData}
                    setFormData={setFormData}
                    serviceType={serviceType}
                    setServiceType={setServiceType}
                    appointmentDate={appointmentDate}
                    setAppointmentDate={setAppointmentDate}
                    doctor={doctor}
                    setDoctor={setDoctor}
                    setPaymentMethod={setPaymentMethod}
                    prePatientId={prePatientId}
                    clinicLabsData={clinicLabsData ?? []}
                    labTypes={labTypes}
                    setLabTypes={setLabTypes}
                    partners={doctorsComboboxItems ?? []}
                  />
                )
              ) : null}

              {newPatientStep === 3 ? (
                <LabPrescriptionStep
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                />
              ) : null}

              <Group mt={"xl"} justify="end" pb="md">
                {currentTab === "new" && newPatientStep > 1 ? (
                  <Button
                    type="button"
                    disabled={isCreateAppointmentLoading}
                    variant="outline"
                    onClick={() => {
                      if (currentTab === "new" && newPatientStep === 1) {
                        return;
                      } else setNewPatientStep(prev => prev - 1);
                    }}>
                    {t("previous")}
                  </Button>
                ) : null}
                <PermissionGate permission="appointments.create"><Button
                  type="submit"
                  disabled={
                    isPhoneInputError ||
                    !formData?.phoneNumber ||
                    formData?.phoneNumber.length < 10 || // To accept both Ghanaian and Ivorian numbers
                    !dob ||
                    isAvailabilityInvalid
                  }
                  loading={isCreateAppointmentLoading}>
                  {currentTab === "new" && newPatientStep === totalSteps
                    ? t("submit")
                    : t("next")}
                </Button></PermissionGate>
              </Group>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value={"existing"}>
            {showExistingPatientForm ? (
              <form className="form" onSubmit={handleFormSubmit}>
                <p className="step-label">
                  {t("step")} {existingPatientStep}
                  {existingPatientStep > 1 &&
                  appointmentCategory?.value === "teleconsultation"
                    ? " - " + t("teleconsultation-appointment")
                    : null}
                  {existingPatientStep > 1 &&
                  appointmentCategory?.value === "homecare"
                    ? " - " + t("homecare-appointment")
                    : null}
                  {existingPatientStep > 1 &&
                  existingPatientStep < 3 &&
                  appointmentCategory?.value === "lab"
                    ? " - " + t("analysis-appointment")
                    : null}
                  {existingPatientStep > 2 &&
                  appointmentCategory?.value === "lab"
                    ? " - " + t("analysis-prescription-documents")
                    : null}
                </p>

                {existingPatientStep === 1 ? (
                  <PersonalInfoStep
                    formData={formData}
                    setFormData={setFormData}
                    setGender={setGender}
                    gender={gender}
                    dob={dob}
                    setDob={setDob}
                    setAppointmentCategory={setAppointmentCategory}
                    isReadOnly
                    setIsPhoneInputError={setIsPhoneInputError}
                    setPhoneNumber={setPhoneNumber}
                  />
                ) : null}

                {existingPatientStep === 2 ? (
                  (appointmentCategory?.value as string) ===
                  "teleconsultation" ? (
                    <TeleconsultationAppointmentStep
                      formData={formData}
                      setFormData={setFormData}
                      setDoctor={setDoctor}
                      doctor={doctor}
                      appointmentDate={appointmentDate}
                      setAppointmentDate={setAppointmentDate}
                      serviceType={serviceType}
                      setServiceType={setServiceType}
                      setPaymentMethod={setPaymentMethod}
                      doctorsForConsultationData={
                        doctorsForConsultationData ?? []
                      }
                      isDoctorsForConsultationLoading={
                        isDoctorsForConsultationLoading &&
                        doctorsForConsultationFetchStatus === "fetching"
                      }
                      isDoctorsForConsultationSuccess={
                        isDoctorsForConsultationSuccess
                      }
                      prePatientId={prePatientId}
                      appointmentCategory={appointmentCategory}
                      slots={availableAppointmentSlots}
                      isSlotsLoading={isAppointmentSlotsLoading}
                      selectedSlotId={selectedSlotId}
                      setSelectedSlotId={setSelectedSlotId}
                    />
                  ) : (appointmentCategory?.value as string) === "homecare" ? (
                    <HomecareAppointmentStep
                      formData={formData}
                      setFormData={setFormData}
                      serviceType={serviceType}
                      setServiceType={setServiceType}
                      appointmentDate={appointmentDate}
                      setAppointmentDate={setAppointmentDate}
                      doctor={doctor}
                      setDoctor={setDoctor}
                      setPaymentMethod={setPaymentMethod}
                      doctorsForConsultationData={
                        doctorsForConsultationData ?? []
                      }
                      isDoctorsForConsultationLoading={
                        isDoctorsForConsultationLoading &&
                        doctorsForConsultationFetchStatus === "fetching"
                      }
                      isDoctorsForConsultationSuccess={
                        isDoctorsForConsultationSuccess
                      }
                      prePatientId={prePatientId}
                      appointmentCategory={appointmentCategory}
                      slots={availableAppointmentSlots}
                      isSlotsLoading={isAppointmentSlotsLoading}
                      selectedSlotId={selectedSlotId}
                      setSelectedSlotId={setSelectedSlotId}
                    />
                  ) : (
                    <LabAppointmentStep
                      formData={formData}
                      setFormData={setFormData}
                      serviceType={serviceType}
                      setServiceType={setServiceType}
                      appointmentDate={appointmentDate}
                      setAppointmentDate={setAppointmentDate}
                      doctor={doctor}
                      setDoctor={setDoctor}
                      setPaymentMethod={setPaymentMethod}
                      prePatientId={prePatientId}
                      clinicLabsData={clinicLabsData ?? []}
                      labTypes={labTypes}
                      setLabTypes={setLabTypes}
                      partners={doctorsComboboxItems ?? []}
                      doctorsForConsultationData={
                        doctorsForConsultationData ?? []
                      }
                    />
                  )
                ) : null}

                {existingPatientStep === 3 ? (
                  <LabPrescriptionStep
                    uploadedFiles={uploadedFiles}
                    setUploadedFiles={setUploadedFiles}
                  />
                ) : null}

                <Group mt={"xl"} justify="space-between">
                  {currentTab === "existing" && showExistingPatientForm ? (
                    <Button
                      variant="transparent"
                      size="compact-sm"
                      onClick={() => {
                        setShowExistingPatientForm(false);
                        // setFormData({} as Partial<ShallowForm>);
                        setAppointmentCategory(null);
                        setExistingPatientStep(1);
                      }}
                      leftSection={<IconArrowLeft size={16} />}>
                      {t("back-to-search")}
                    </Button>
                  ) : null}
                  <Group justify="end" pb="md">
                    {currentTab === "existing" && existingPatientStep > 1 ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (
                            existingPatientStep === 1 &&
                            currentTab === "existing"
                          ) {
                            return;
                          } else {
                            setExistingPatientStep(existingPatientStep - 1);
                          }
                        }}>
                        {t("previous")}
                      </Button>
                    ) : null}
                    <PermissionGate permission="appointments.create"><Button
                      disabled={isAvailabilityInvalid}
                      loading={isCreateAppointmentLoading}
                      type="submit">
                      {currentTab === "existing" &&
                      existingPatientStep === totalSteps
                        ? t("submit")
                        : t("next")}
                    </Button></PermissionGate>
                  </Group>
                </Group>
              </form>
            ) : (
              <div className="search-section">
                <SearchIllustration className="illustration" />
                <h2>{t("search-for-patient")}</h2>
                <PatientsSelect
                  w={"63%"} // NB: Have to use magic number (number slightly bigger dropdown width) because dropdown is slightly larger for some reason
                  mx="auto"
                  mt="md"
                  leftSection={
                    isPatientsLoading ? (
                      <Loader size={16} color="accent.4" />
                    ) : (
                      <IconSearch size={16} />
                    )
                  }
                  rightSectionPointerEvents="unset"
                  rightSection={
                    <ActionIcon
                      disabled={!searchFilter}
                      onClick={() => {
                        if (getPatientData) {
                          setShowExistingPatientForm(true);
                        } else {
                          getPatientRefetch();
                        }
                      }}
                      loaderProps={{ type: "oval" }}
                      loading={
                        isGetPatientLoading &&
                        getPatientFetchStatus === "fetching"
                      }>
                      <IconArrowRight size={16} />
                    </ActionIcon>
                  }
                  data={patientsComboboxItems ?? []}
                  value={searchFilter}
                  setValue={setSearchFilter}
                />
              </div>
            )}
          </Tabs.Panel>
        </Tabs>
      </StyledCreateAppointmentModalContent>
    </Modal>
  );
};

interface PersonalInfoStepProps {
  formData: Partial<ShallowForm>;
  setFormData: (arg: Partial<ShallowForm>) => void;
  gender: string | null;
  setGender: (arg: string | null) => void;
  setAppointmentCategory: (arg: ComboboxItem | null) => void;
  dob: Date | null;
  setDob: (arg: Date | null) => void;
  isReadOnly?: boolean;
  setPhoneNumber: (arg: string) => void;
  setIsPhoneInputError: (arg: boolean) => void;
}

const PersonalInfoStep = ({
  formData,
  setFormData,
  gender,
  setGender,
  dob,
  setDob,
  setAppointmentCategory,
  isReadOnly = false,
  setPhoneNumber,
  setIsPhoneInputError,
}: PersonalInfoStepProps) => {
  const { t } = useTranslation();
  const { isSuccess: isExistingPhoneNumber } = useGetPatientByPhoneNumber({
    phoneNumber: formData.phoneNumber ?? "",
    isEnabled: (formData?.phoneNumber ?? "").length >= 10,
  });
  return (
    <div className="form-step">
      <div className="form-group">
        <div className="form-field">
          <label htmlFor="first-name">
            {t("first-name")}
            <sup>*</sup>
          </label>
          <input
            type="text"
            placeholder={t("eg", { example: "John" })}
            id="first-name"
            onChange={e => handleOnChange(e, setFormData, formData)}
            value={formData?.firstName ?? ""}
            required
            name="firstName"
            disabled={isReadOnly}
          />
        </div>
        <div className="form-field">
          <label htmlFor="surname">
            {t("surname")}
            <sup>*</sup>
          </label>
          <input
            type="text"
            placeholder={t("eg", { example: "Doe" })}
            id="Surname"
            required
            name="surname"
            onChange={e => handleOnChange(e, setFormData, formData)}
            value={formData?.surname ?? ""}
            disabled={isReadOnly}
          />
        </div>
      </div>

      <div className="form-group">
        <div className="form-field">
          <label htmlFor="phone">
            {t("phone-number")}
            <sup>*</sup>
          </label>
          {isReadOnly ? (
            <input
              type="tel"
              placeholder={t("eg", { example: "+250 788 888 888" })}
              id="phone"
              required
              onChange={e => handleOnChange(e, setFormData, formData)}
              value={formData?.phoneNumber ?? ""}
              name="phoneNumber"
              disabled={isReadOnly}
            />
          ) : (
            <CustomPhoneInput
              id="phone"
              phone={formData?.phoneNumber ?? ""}
              setPhone={setPhoneNumber}
              required
              setIsError={setIsPhoneInputError}
              isExistingNumber={isExistingPhoneNumber}
            />
          )}
        </div>
        <div className="form-field">
          <DatePickerInput
            label={t("dob")}
            value={dob}
            onChange={setDob}
            withAsterisk
            disabled={isReadOnly}
            leftSection={<IconCalendar size={16} stroke={1.5} />}
            maxDate={new Date()}
          />
        </div>
      </div>

      <div className="form-group">
        <div className="form-field">
          <Select
            label={t("gender")}
            placeholder={t("select-gender")}
            data={getGenderValues()}
            withAsterisk
            value={gender}
            onChange={setGender}
            disabled={isReadOnly}
          />
        </div>
        <div className="form-field">
          <Select
            label={t("appointment-category")}
            placeholder={t("select-category")}
            data={getAppointmentCategoriesValues()}
            withAsterisk
            value={formData?.category}
            onChange={(_val, option) => setAppointmentCategory(option)}
          />
        </div>
      </div>
    </div>
  );
};

interface AppointmentAvailabilityFieldsProps {
  appointmentDate: Date | null;
  setAppointmentDate: (arg: Date | null) => void;
  doctor: ComboboxItem | null;
  slots: AppointmentSlot[];
  isSlotsLoading: boolean;
  selectedSlotId: string;
  setSelectedSlotId: (slotId: string) => void;
}

const AppointmentAvailabilityFields = ({
  appointmentDate,
  setAppointmentDate,
  doctor,
  slots,
  isSlotsLoading,
  selectedSlotId,
  setSelectedSlotId,
}: AppointmentAvailabilityFieldsProps) => {
  const { t, i18n } = useTranslation();
  const selectedDay = appointmentDate
    ? new Date(
        appointmentDate.getFullYear(),
        appointmentDate.getMonth(),
        appointmentDate.getDate()
      )
    : null;
  const slotsForSelectedDay = selectedDay
    ? slots.filter(slot => slot.startTime.toDateString() === selectedDay.toDateString())
    : [];

  return (
    <>
      <div className="form-group">
        <div className="form-field">
          <DatePickerInput
            label={t("available-date")}
            placeholder={t("select-date")}
            value={selectedDay}
            onChange={date => {
              setAppointmentDate(date);
              setSelectedSlotId("");
            }}
            disabled={!doctor || isSlotsLoading}
            rightSection={isSlotsLoading ? <Loader size={16} /> : <IconCalendar size={16} />}
            minDate={new Date()}
            excludeDate={date =>
              !slots.some(slot => slot.startTime.toDateString() === date.toDateString())
            }
            withAsterisk
          />
        </div>
        <div className="form-field">
          <Select
            label={t("available-time")}
            placeholder={t("select-time")}
            value={selectedSlotId || null}
            onChange={value => {
              const slot = slots.find(item => item.id === value);
              setSelectedSlotId(value ?? "");
              setAppointmentDate(slot?.startTime ?? null);
            }}
            data={slotsForSelectedDay.map(slot => ({
              value: slot.id,
              label: slot.startTime.toLocaleTimeString(i18n.language, {
                hour: "2-digit",
                minute: "2-digit",
              }),
            }))}
            disabled={!selectedDay || isSlotsLoading}
            withAsterisk
          />
        </div>
      </div>
      {doctor && !isSlotsLoading && slots.length === 0 ? (
        <Alert color="orange" icon={<IconInfoCircle size={16} />}>
          {t("no-available-slots-for-doctor")}
        </Alert>
      ) : null}
    </>
  );
};

interface TeleconsultationAppointmentStepProps
  extends Partial<PersonalInfoStepProps> {
  appointmentDate: Date | null;
  setAppointmentDate: (arg: Date | null) => void;
  doctor: ComboboxItem | null;
  setDoctor: React.Dispatch<React.SetStateAction<ComboboxItem | null>>;
  serviceType: ComboboxItem | null;
  setServiceType: (arg: ComboboxItem | null) => void;
  setPaymentMethod: (arg: string | null) => void;
  doctorsForConsultationData?: ComboboxItem[] | [];
  isDoctorsForConsultationLoading?: boolean;
  isDoctorsForConsultationSuccess?: boolean;
  prePatientId?: string;
  appointmentCategory?: ComboboxItem | null;
  slots: AppointmentSlot[];
  isSlotsLoading: boolean;
  selectedSlotId: string;
  setSelectedSlotId: (slotId: string) => void;
}

const TeleconsultationAppointmentStep = ({
  appointmentDate,
  setAppointmentDate,
  doctor,
  setDoctor,
  serviceType,
  setServiceType,
  setFormData,
  formData,
  setPaymentMethod,
  doctorsForConsultationData,
  isDoctorsForConsultationLoading,
  isDoctorsForConsultationSuccess,
  prePatientId,
  appointmentCategory,
  slots,
  isSlotsLoading,
  selectedSlotId,
  setSelectedSlotId,
}: TeleconsultationAppointmentStepProps) => {
  const { t } = useTranslation();
  const { data: consultationServicesData } = useGetClinicConsultationServices({
    category: appointmentCategory?.value ?? "",
  });

  return (
    <div className="form-step">
      <div className="form-group">
        <div className="form-field">
          <Select
            label={t("service-type")}
            placeholder={t("select-service-type")}
            data={consultationServicesData || []}
            value={serviceType?.value ?? null}
            onChange={(_val, option) => {
              setServiceType(option);
            }}
            required
            searchable
            comboboxProps={{ zIndex: 999999 }}
          />
        </div>
      </div>

      <div className="form-group">
        <div className="form-field">
          <Select
            label={t("doctor")}
            withAsterisk
            searchable
            description={
              serviceType?.value
                ? isDoctorsForConsultationSuccess &&
                  !doctorsForConsultationData?.length
                  ? t("no-doctors-for-service")
                  : ""
                : t("select-service-to-populate-doctors")
            }
            disabled={
              !serviceType ||
              (isDoctorsForConsultationSuccess &&
                !doctorsForConsultationData?.length)
            }
            placeholder={t("select-doctor")}
            value={doctor?.value ?? null}
            onChange={(_val, option) => setDoctor(option)}
            rightSection={
              isDoctorsForConsultationLoading ? <Loader size={16} /> : null
            }
            required
            data={doctorsForConsultationData}
            comboboxProps={{ zIndex: 999999 }}
          />
        </div>
        <div className="form-field">
          <label htmlFor="city">{t("city")}</label>
          <input
            type="text"
            name="city"
            id="city"
            onChange={e => {
              if (setFormData && formData)
                handleOnChange(e, setFormData, formData);
            }}
            value={formData?.city ?? ""}
          />
        </div>
      </div>

      <AppointmentAvailabilityFields
        appointmentDate={appointmentDate}
        setAppointmentDate={setAppointmentDate}
        doctor={doctor}
        slots={slots}
        isSlotsLoading={isSlotsLoading}
        selectedSlotId={selectedSlotId}
        setSelectedSlotId={setSelectedSlotId}
      />

      {prePatientId ? null : (
        <div className="form-field half">
          <Select
            label={t("payment-method")}
            placeholder={t("select-method")}
            data={getPaymentMethods()}
            value={formData?.paymentMethod}
            onChange={setPaymentMethod}
          />
        </div>
      )}
    </div>
  );
};

interface HomecareAppointmentStepProps
  extends TeleconsultationAppointmentStepProps {
  serviceType: ComboboxItem | null;
  setServiceType: (arg: ComboboxItem | null) => void;
}

const HomecareAppointmentStep = ({
  formData,
  setFormData,
  appointmentDate,
  setAppointmentDate,
  setDoctor,
  doctor,
  serviceType,
  setServiceType,
  setPaymentMethod,
  doctorsForConsultationData,
  isDoctorsForConsultationLoading,
  isDoctorsForConsultationSuccess,
  prePatientId,
  appointmentCategory,
  slots,
  isSlotsLoading,
  selectedSlotId,
  setSelectedSlotId,
}: HomecareAppointmentStepProps &
  Pick<PersonalInfoStepProps, "setFormData" | "formData">) => {
  const { t } = useTranslation();
  const { data: consultationServicesData } = useGetClinicConsultationServices({
    category: appointmentCategory?.value ?? "",
  });

  return (
    <div className="form-step">
      <div className="form-group">
        <div className="form-field">
          <Select
            label={t("service-type")}
            placeholder={t("select-service-type")}
            data={consultationServicesData || []}
            value={serviceType?.value ?? null}
            onChange={(_val, option) => {
              setServiceType(option);
            }}
            required
            searchable
            comboboxProps={{ zIndex: 999999 }}
          />
        </div>
      </div>

      <div className="form-group">
        <div className="form-field">
          <Select
            label={t("doctor")}
            withAsterisk
            searchable
            description={
              serviceType?.value
                ? isDoctorsForConsultationSuccess &&
                  !doctorsForConsultationData?.length
                  ? t("no-doctors-for-service")
                  : ""
                : t("select-service-to-populate-doctors")
            }
            disabled={
              !serviceType ||
              (isDoctorsForConsultationSuccess &&
                !doctorsForConsultationData?.length)
            }
            placeholder={t("select-doctor")}
            value={doctor?.value ?? null}
            onChange={(_val, option) => setDoctor(option)}
            rightSection={
              isDoctorsForConsultationLoading ? <Loader size={16} /> : null
            }
            required
            data={doctorsForConsultationData}
            comboboxProps={{ zIndex: 999999 }}
          />
        </div>

        <div className="form-field">
          <label htmlFor="city">{t("city")}</label>
          <input
            type="text"
            name="city"
            id="city"
            onChange={e => handleOnChange(e, setFormData, formData)}
            value={formData?.city ?? ""}
          />
        </div>
      </div>

      <AppointmentAvailabilityFields
        appointmentDate={appointmentDate}
        setAppointmentDate={setAppointmentDate}
        doctor={doctor}
        slots={slots}
        isSlotsLoading={isSlotsLoading}
        selectedSlotId={selectedSlotId}
        setSelectedSlotId={setSelectedSlotId}
      />

      <div className="form-group">
        <div className="form-field">
          <label htmlFor="zone">{t("zone")}</label>
          <input
            type="text"
            name="zone"
            id="zone"
            onChange={e => handleOnChange(e, setFormData, formData)}
            value={formData?.zone ?? ""}
          />
        </div>

        {prePatientId ? null : (
          <div className="form-field">
            <Select
              label={t("payment-method")}
              placeholder={t("select-method")}
              data={getPaymentMethods()}
              value={formData?.paymentMethod}
              onChange={setPaymentMethod}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface LabAppointmentStepProps
  extends Partial<TeleconsultationAppointmentStepProps> {
  clinicLabsData: ComboboxItem[];
  labTypes: ComboboxItem[];
  setLabTypes: React.Dispatch<React.SetStateAction<ComboboxItem[]>>;
  partners: ComboboxItem[];
}
const LabAppointmentStep = ({
  formData,
  setFormData,
  appointmentDate,
  setAppointmentDate,
  setPaymentMethod,
  prePatientId,
  clinicLabsData,
  labTypes,
  setLabTypes,
  doctor,
  setDoctor,
  partners,
}: LabAppointmentStepProps &
  Pick<PersonalInfoStepProps, "setFormData" | "formData">) => {
  const { t } = useTranslation();
  const timePickerRef = useRef<HTMLInputElement>(null);

  return (
    <div className="form-step">
      <div className="form-group">
        <div className="form-field">
          <DateTimePicker
            label={t("appointment-date")}
            value={appointmentDate}
            onChange={setAppointmentDate}
            withAsterisk
            timeInputProps={{
              ref: timePickerRef,
              leftSection: (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => timePickerRef?.current?.showPicker()}>
                  <IconClock size={16} />
                </ActionIcon>
              ),
              minTime: "08:00:00",
              maxTime: "17:00:00",
            }}
            minDate={new Date()}
          />
        </div>
        <div className="form-field">
          <MultiSelectWithSearch
            label={t("lab-type")}
            placeholder={t("select-lab-types")}
            searchPlaceholder={t("search-lab-type")}
            // withAsterisk
            value={labTypes}
            setValue={setLabTypes}
            // isLoading={isClinicLabsLoading || isClinicLabsRefetching}
            data={clinicLabsData ?? []}
            withinPortal
          />
        </div>
      </div>

      <div className="form-group">
        <div className="form-field">
          <Select
            label={t("partner")}
            withAsterisk
            searchable
            value={doctor?.value ?? null}
            onChange={(_val, option) => {
              if (setDoctor) setDoctor(option);
            }}
            data={partners}
            // required
            comboboxProps={{ zIndex: 999999 }}
          />
        </div>

        <div className="form-field">
          <label htmlFor="city">{t("city")}</label>
          <input
            type="text"
            name="city"
            id="city"
            onChange={e => handleOnChange(e, setFormData, formData)}
            value={formData?.city ?? ""}
          />
        </div>
      </div>

      <div className="form-group">
        {prePatientId ? null : (
          <div className="form-field">
            <Select
              label={t("payment-method")}
              placeholder={t("select-method")}
              data={getPaymentMethods()}
              value={formData?.paymentMethod}
              onChange={setPaymentMethod}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface LabPrescriptionStepProps {
  setUploadedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  uploadedFiles: File[];
}

const LabPrescriptionStep = ({
  uploadedFiles,
  setUploadedFiles,
}: LabPrescriptionStepProps) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };
  const { t } = useTranslation();

  return (
    <div className="files-wrapper">
      <div
        {...getRootProps({
          className: `dropzone ${isDragActive ? "active" : ""} ${
            isDragAccept ? "accept" : ""
          } ${isDragReject ? "reject" : ""}`,
        })}>
        <input {...getInputProps()} required name="dropzone" />

        <div className="text-content">
          <div className="svg-wrapper">
            <LiaFileUploadSolid />
          </div>
          <div className="p-wrapper">
            <p>{t("drag-file-here")}</p>
            <p>{t("drag-file-here-continuation")}</p>
          </div>
        </div>
      </div>
      <div className="uploaded-files">
        <h4>{t("uploaded-files")}</h4>
        {!uploadedFiles.length ? (
          <Alert
            mt={"md"}
            variant="light"
            color="blue"
            icon={<IconInfoCircle />}>
            <Text fz={14} lh={1.5} c="blue">
              {t("files-appear-info")}
            </Text>
          </Alert>
        ) : (
          <ScrollAreaAutosize h={200} type="auto" mt="sm">
            <ul>
              {uploadedFiles.map(file => (
                <li key={file.name}>
                  <div className="content">
                    {file.type === "application/pdf" ? (
                      <AiOutlineFilePdf />
                    ) : (
                      <AiOutlineFileImage />
                    )}
                    <p>
                      {truncateWithEllipsis(
                        file.name,
                        MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                      )}{" "}
                      ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                    </p>
                  </div>

                  <ActionIcon
                    variant="outline"
                    color="red"
                    onClick={() => removeFile(file)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </li>
              ))}
            </ul>
          </ScrollAreaAutosize>
        )}
      </div>
    </div>
  );
};
